import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { MassNotificationTemplateGroup } from '../../store/communications/communications.model';
import { CommunicationsApiService } from '../../store/communications/communications-api.service';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { GUID_EMPTY } from '@app/core/constants';

export type TemplateGroupsState = {
  isWorking: boolean;
  templateGroups: MassNotificationTemplateGroup[];
};

const initialState: TemplateGroupsState = {
  isWorking: false,
  templateGroups: []
};

@Injectable()
export class TemplateGroupsCRUDModalStore extends ComponentStore<TemplateGroupsState> {
  private api = inject(CommunicationsApiService);
  private notify = inject(SystemNotificationsService);
  private translate = inject(TranslateService);

  readonly templateGroups = this.selectSignal(state => state?.templateGroups ?? []);
  readonly isWorking = this.selectSignal(state => state?.isWorking ?? false);

  constructor() {
    super(initialState);
    this.getTemplateGroups();
  }

  private getTemplateGroups(): void {
    this.patchState({ isWorking: true });

    this.api
      .getMassNotificationTemplateGroups({
        skip: 0,
        take: 1000,
        where: ``,
        sortField: 'Name',
        sortDirection: 'Ascending'
      })
      .subscribe({
        next: ({ data }) => this.patchState({ templateGroups: data }),
        error: err => {
          console.error(err);
          this.patchState({ templateGroups: [] });
          this.notify.error(this.translate.instant('ERRORS.GENERAL'));
        },
        complete: () => {
          this.patchState({ isWorking: false });
        }
      });
  }

  createTemplateGroup(name: string): void {
    this.patchState({ isWorking: true });

    this.api
      .createMassNotificationTemplateGroup({
        uId: GUID_EMPTY,
        name
      })
      .subscribe({
        next: () => {
          this.notify.success(this.translate.instant('MANAGEMENT.COMMUNICATION-CENTER.TEMPLATE-GROUP-CREATE-SUCCESS'));
          this.getTemplateGroups();
        },
        error: err => {
          console.error(err);
          this.patchState({ isWorking: false });
          this.notify.error(this.translate.instant('ERRORS.GENERAL'));
        }
      });
  }

  updateTemplateGroup(templateGroup: MassNotificationTemplateGroup): void {
    this.patchState({ isWorking: true });

    this.api.updateMassNotificationTemplateGroup(templateGroup).subscribe({
      next: () => {
        this.notify.success(this.translate.instant('MANAGEMENT.COMMUNICATION-CENTER.TEMPLATE-GROUP-EDIT-SUCCESS'));
        this.getTemplateGroups();
      },
      error: err => {
        console.error(err);
        this.patchState({ isWorking: false });
        this.notify.error(this.translate.instant('ERRORS.GENERAL'));
      }
    });
  }

  deleteTemplateGroup({ uId }: MassNotificationTemplateGroup): void {
    this.patchState({ isWorking: true });

    this.api.deleteMassNotificationTemplateGroup(uId).subscribe({
      next: () => { 
        this.notify.success(this.translate.instant('MANAGEMENT.COMMUNICATION-CENTER.TEMPLATE-GROUP-DELETE-SUCCESS'));
        this.getTemplateGroups();
      },
      error: err => {
        console.error(err);
        this.patchState({ isWorking: false });
        this.notify.error(this.translate.instant('ERRORS.GENERAL'));
      }
    });
  }

  checkIfNameIsUnique(value: string): boolean {
    // value has to be at least 2 characters long excluding leading & trailing whitespace
    if (!Boolean(value.trim()) || value.trim().length < 2) {
      return false;
    }

    const templates = this.templateGroups();
    return templates.every(({ name }) => name.toLocaleLowerCase() !== value.toLocaleLowerCase());
  }
}
